/**
 * Node Browser Pane Component
 * Main container for LoadSEER API integration
 * Orchestrates authentication and resource type selection
 *
 * ARCHITECTURE:
 * - Resource type tabs allow switching between different data types
 * - Each resource type has its own view component (see resource-types/)
 * - Shared state (auth, loading) managed at this level
 * - Type-specific logic delegated to view components
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { FC } from 'react';
import styled from 'styled-components';
import type { LoadSEERApiClient } from '../api';
import type { NodeTypeDto } from '../types';
import { LoadSEERProvider } from './LoadSEERProvider';
import { RESOURCE_TYPES, getDefaultResourceType } from './resource-types/registry';
import type { ResourceTypeConfig } from './resource-types/registry';

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 1rem 1rem 1rem;
  background: ${props => props.theme?.panelBackground || '#0d1117'};
  color: ${props => props.theme?.textColor || '#e6edf3'};
  height: 100%;
  overflow-y: auto;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &.scrollable {
    max-height: 500px;
    overflow-y: auto;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme?.textColor || '#e6edf3'};
  margin: 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${props => props.theme?.borderColor || '#30363d'};
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 2px solid ${props => props.theme?.borderColor || '#30363d'};
  margin-bottom: 1rem;
  gap: 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 0.75rem 1rem;
  background: transparent;
  color: ${props =>
    props.$active ? props.theme?.textColor || '#e6edf3' : props.theme?.subtextColor || '#8b949e'};
  border: none;
  border-bottom: 2px solid
    ${props => (props.$active ? props.theme?.buttonBgd || '#238636' : 'transparent')};
  margin-bottom: -2px;
  font-size: 0.875rem;
  font-weight: ${props => (props.$active ? '600' : '500')};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: ${props => props.theme?.textColor || '#e6edf3'};
    background: ${props => props.theme?.panelBackgroundHover || '#21262d33'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ============================================================================
// Component
// ============================================================================

export const NodeBrowserPane: FC = () => {
  // Authentication state
  const [apiClient, setApiClient] = useState<LoadSEERApiClient | null>(null);
  const [nodeTypes, setNodeTypes] = useState<NodeTypeDto[]>([]);

  // Resource type selection
  const [activeResourceType, setActiveResourceType] =
    useState<ResourceTypeConfig>(getDefaultResourceType());

  // Shared loading state across all resource types
  const [isLoading, setIsLoading] = useState(false);

  // Fetch node types when API client is authenticated
  useEffect(() => {
    if (apiClient) {
      apiClient
        .getNodeTypes()
        .then(response => {
          // TODO: Re-enable filtering once API bug is fixed (defaultLabels not always populated)
          // Filter to only include node types with 'grid-asset' in defaultLabels
          // const gridAssetTypes = response.results.filter(nodeType =>
          //   nodeType.defaultLabels?.includes('grid-asset')
          // );
          // setNodeTypes(gridAssetTypes);

          // Temporary: Show all node types until API bug is fixed
          setNodeTypes(response.results);
        })
        .catch(err => {
          if (import.meta.env.DEV) {
            console.warn('Failed to fetch node types:', err);
          }
        });
    }
  }, [apiClient]);

  // Handle authentication
  const handleAuthenticated = useCallback((client: LoadSEERApiClient) => {
    setApiClient(client);
  }, []);

  // Handle load completion (callback from resource views)
  const handleLoadComplete = useCallback(() => {
    // Future: could trigger analytics, refresh, etc.
  }, []);

  // Handle resource type change
  const handleResourceTypeChange = useCallback((type: ResourceTypeConfig) => {
    setActiveResourceType(type);
  }, []);

  // Render active resource view component
  const ActiveViewComponent = activeResourceType.component;

  return (
    <Container>
      <Section className='scrollable'>
        <SectionTitle>LoadSEER API Connection</SectionTitle>
        <LoadSEERProvider onAuthenticated={handleAuthenticated} />
      </Section>

      {apiClient && (
        <>
          <TabContainer>
            {RESOURCE_TYPES.map(type => (
              <Tab
                key={type.id}
                $active={activeResourceType.id === type.id}
                onClick={() => handleResourceTypeChange(type)}
                disabled={isLoading}
              >
                {type.label}
              </Tab>
            ))}
          </TabContainer>

          <ActiveViewComponent
            apiClient={apiClient}
            nodeTypes={nodeTypes}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            onLoadComplete={handleLoadComplete}
          />
        </>
      )}
    </Container>
  );
};
